import { WebStorageStateStore } from "oidc-client-ts";
import sharedConfig from "../../shared-config.json";

const { issuer, clientId, projectId, orgId } = sharedConfig.auth;

const getRedirectOrigin = () => {
  if (typeof window !== "undefined") return window.location.origin;
  const env = import.meta.env || {};
  if (env.PROD) {
    return env.VITE_BASE_URL || sharedConfig.urls.production.web;
  }
  return sharedConfig.urls.development.web;
};

/**
 * OIDC UserManager settings for Zitadel.
 *
 * IdP IDs no longer baked into the static bundle — they're returned
 * by the backend's /api/get-invite-context only after a valid
 * invitation token, and used at runtime via buildIdpScopeRequest().
 * That keeps an unauthenticated visitor from constructing an IdP
 * login URL just by reading our shipped JS.
 */
export const zitadelConfig = {
  authority: issuer,
  client_id: clientId,
  redirect_uri: `${getRedirectOrigin()}/auth/callback`,
  silent_redirect_uri: `${getRedirectOrigin()}/silent-renew`,
  post_logout_redirect_uri: getRedirectOrigin(),
  response_type: "code",
  scope: [
    "openid",
    "profile",
    "email",
    "offline_access",
    `urn:zitadel:iam:org:project:id:${projectId}:aud`,
    "urn:zitadel:iam:org:projects:roles",
  ].join(" "),
  automaticSilentRenew: true,
  loadUserInfo: true,
  userStore:
    typeof window !== "undefined"
      ? new WebStorageStateStore({ store: window.localStorage })
      : undefined,
};

const baseScopes = (extra = []) =>
  [
    "openid",
    "profile",
    "email",
    "offline_access",
    `urn:zitadel:iam:org:project:id:${projectId}:aud`,
    "urn:zitadel:iam:org:projects:roles",
    ...extra,
  ].join(" ");

/**
 * Build the OIDC signinRedirect args that auto-route to a specific IdP.
 *
 * Zitadel's V2 login UI honours `urn:zitadel:iam:org:idp:id:<idpId>`
 * as a scope and routes straight to that IdP, skipping its own
 * provider chooser. **But the IDP scope alone is not enough** — the
 * login UI calls getActiveIdentityProviders(orgId), and if no
 * `urn:zitadel:iam:org:id:<orgId>` scope is present it queries at
 * instance level. Our IdPs (Google, Microsoft personal, Delosis
 * Entra) are org-level, so without the org scope the lookup misses
 * them and the chooser falls through. Both scopes required.
 *
 * (See apps/login/src/lib/server/flow-initiation.ts and
 *  apps/login/src/lib/zitadel.ts in zitadel/zitadel — IDP_SCOPE_REGEX
 *  and getActiveIdentityProviders → makeReqCtx.)
 */
export const buildIdpScopeRequest = (idpId) =>
  idpId
    ? {
        scope: baseScopes([
          `urn:zitadel:iam:org:id:${orgId}`,
          `urn:zitadel:iam:org:idp:id:${idpId}`,
        ]),
      }
    : null;

/**
 * Login-method requests for the LoginDialog (return users) — a thin
 * compatibility layer around buildIdpScopeRequest. The dialog will
 * only be able to populate these once it's pulled IdP IDs from the
 * backend (an upcoming change for return-login flows; for now invitee
 * flows are handled directly by /invite via start-invite-redemption).
 *
 * All requests include the org-id scope. Without it, Zitadel's login UI
 * falls back to instance-level IdPs and shows the user every Microsoft,
 * Google, etc. wired up across the whole instance — not just the IdPs
 * the PRADAM org has configured. See buildIdpScopeRequest above for the
 * upstream reference to getActiveIdentityProviders(orgId).
 */
const orgScopedBase = () => baseScopes([`urn:zitadel:iam:org:id:${orgId}`]);

export const loginRequests = {
  email: () => ({ scope: orgScopedBase() }),
  signup: () => ({
    scope: orgScopedBase(),
    extraQueryParams: { prompt: "create" },
  }),
  passwordReset: () => ({
    scope: orgScopedBase(),
    extraQueryParams: { prompt: "reset" },
  }),
};
